import { Alert, Form, Input, Modal, Select } from 'antd';
import { toZoneCode } from '../policies/zone-code';
import type { Zone } from '../zones.api';
import { useZoneQuickCreate } from './use-zone-quick-create';

interface ZoneQuickCreateModalProps {
  open: boolean;
  onCreated: (zone: Zone) => void;
  onCancel: () => void;
}

/**
 * Starts a zone from another unit's form (a garage's or supplier's zone picker) with only a name, code and kind.
 * It has no location yet: place it on the Zones map later, which also works out its country.
 */
export function ZoneQuickCreateModal({ open, onCreated, onCancel }: ZoneQuickCreateModalProps) {
  const [form] = Form.useForm<{ name: string; code: string; kind: string }>();
  const { kinds, error, saving, create } = useZoneQuickCreate(open);

  async function submit(values: { name: string; code: string; kind: string }) {
    const zone = await create(values);
    if (!zone) return;
    form.resetFields();
    onCreated(zone);
  }

  return (
    <Modal open={open} title="New zone" okText="Create zone" onOk={() => form.submit()} onCancel={onCancel} confirmLoading={saving} destroyOnHidden>
      {error && <Alert className="mb-4" type="error" title={error} showIcon />}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        onValuesChange={(changed) => {
          if (changed.name !== undefined && !form.isFieldTouched('code')) form.setFieldValue('code', toZoneCode(changed.name));
        }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Choose a kind' }]} extra="Add its location later on the Zones map.">
          <Select placeholder="Select a kind" options={kinds.map((kind) => ({ value: kind.code, label: kind.name }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
